import type { SyntheticEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Autocomplete, TextField } from '@mui/material';
import debounce from 'lodash.debounce';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ErrorIcon } from '@icons/ErrorIcon';
import { type TenantMemberFilter } from '@repositories/tenantRepository/tenant.types';
import { useGetTenantMember, useGetTenantMembersInfinite } from '@queryHooks/useTenant';
import { DomUtil } from '@utils/DomUtil';
import type { TenantMemberSimpleModel } from '@repositories/tenantMemberRepository/models/TenantMemberSimpleModel';

type Props = {
  value: string | null | undefined;
  onChange: (memberId: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  getOptionLabel?: (option: TenantMemberSimpleModel) => string;
  getOptionDisabled?: (option: TenantMemberSimpleModel) => boolean;
  paperWidth?: string;
  listboxHeight?: string;
};

export const MemberAutocomplete = ({
  value,
  onChange,
  disabled = false,
  error = false,
  placeholder,
  fullWidth = false,
  getOptionLabel,
  getOptionDisabled,
  paperWidth = '544px',
  listboxHeight = '208px',
}: Props) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { authStore } = useStore();

  // NOTE. 삭제되지 않앗고, 퇴사 상태가 아니어야함.
  const [memberFilter, setMemberFilter] = useState<TenantMemberFilter>({
    isDeleted: false,
    tenantMemberStatus: 'ACTIVE',
  });

  const {
    data: pagedUserGroupMembers,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetTenantMembersInfinite(authStore.curTenant.id, memberFilter);

  const { data: selectedMember } = useGetTenantMember(authStore.curTenant.id, value ?? '', { enabled: !!value });

  const userGroupMembers = pagedUserGroupMembers?.pages.map(({ content }) => content).flat(1) ?? [];

  const handleMemberSearchChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberFilter(prev => ({ ...prev, keyword: e.target.value }));
  }, 1000);

  const handleMemberListScroll = debounce((event: React.UIEvent<HTMLUListElement>) => {
    if (DomUtil.isScrollBottom(event.target as HTMLUListElement) && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, 100);

  const handleChange = (_event: SyntheticEvent<Element, Event>, selectedValue: TenantMemberSimpleModel | null) => {
    onChange(selectedValue?.id ?? null);
  };

  const selectedValue = selectedMember ?? null;

  return (
    <Autocomplete
      value={selectedValue}
      onChange={handleChange}
      options={userGroupMembers}
      getOptionLabel={getOptionLabel ?? (option => (option.id ? `${option.name} (${option.email})` : ''))}
      getOptionDisabled={getOptionDisabled}
      disabled={disabled}
      fullWidth={fullWidth}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder ?? t('Subscrib_Edit_12')}
          variant="outlined"
          error={error}
          onChange={handleMemberSearchChange}
          disabled={disabled}
          sx={{
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderRadius: '2px 2px 0 0',
            },
            '& .MuiInputBase-root': {
              '& .MuiAutocomplete-endAdornment': {
                width: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: '0',
                gap: '6px',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                '& ~ &': {
                  justifyContent: 'flex-start',
                },
              },
              '& .MuiButtonBase-root': {
                width: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: '0',
                padding: '0',
              },
              '& .MuiSvgIcon-root': {
                width: '18px',
                height: '18px',
                color: `${colors['ic-gray-dark']}`,
              },
              '& .MuiTouchRipple-root': {
                display: 'none',
              },
              '&.Mui-disabled': {
                '& .MuiOutlinedInput-notchedOutline ': {
                  borderColor: `${colors['border-gray-light']}`,
                },
                '& .MuiSvgIcon-root': {
                  color: `${colors['ic-gray-light']}`,
                },
              },
            },
          }}
        />
      )}
      ListboxProps={{ onScroll: handleMemberListScroll, role: 'list-box' }}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      noOptionsText={<AutocompleteNoOptions />}
      sx={{
        '& .MuiInputBase-root': {
          padding: '10px 12px',
        },
        '& .MuiButtonBase-root': {
          borderRadius: '0',
          '&:hover': {
            backgroundColor: `${colors['bg-white']}`,
          },
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          padding: 0,
        },
      }}
      componentsProps={{
        paper: {
          sx: {
            width: paperWidth,
            marginTop: 0,
            borderRadius: '0 0 2px 2px',
            boxShadow: '0',
            border: `1px solid ${colors['border-gray-light']}`,
            borderWidth: '0 1px 1px 1px',
            overflow: 'hidden',
            '& .MuiAutocomplete-listbox': {
              ...(listboxHeight === 'auto' ? { height: 'auto' } : { height: listboxHeight, maxHeight: listboxHeight }),
            },
            '& .MuiList-root': {
              border: 'none',
              padding: '4px 0',
            },
            '& .MuiAutocomplete-option': {
              margin: '0 5px',
            },
          },
        },
      }}
    />
  );
};

const AutocompleteNoOptions = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <AutocompleteNoOptionsContainer>
      <ErrorIconWrapper>
        <ErrorIcon width={22} height={22} color={colors['ic-primary-light']} />
      </ErrorIconWrapper>
      <p>{t('Subscrib_Edit_11')}</p>
    </AutocompleteNoOptionsContainer>
  );
};

const AutocompleteNoOptionsContainer = styled.div`
  padding: 16px 0px;
  ${fonts.Body2}
  text-align: center;
`;

const ErrorIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme: { colors } }) => colors['bg-primary-lighter']};
  margin: 0 auto 12px;
  padding: 7px;
`;
